import React, { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE', 'ACTIVEPERMANENTLY'];

const STATUS_COLOR = {
  ACTIVE:            '#3fb950',
  ACTIVEPERMANENTLY: '#58a6ff',
  INACTIVE:          '#484f58',
};

const OrderbookStreaming = () => {
  const { user } = useAuth();

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const [symbolFilter, setSymbolFilter]     = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [statusFilter, setStatusFilter]     = useState('');
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState('50');

  // per-row pending status selection & saving state
  const [pendingStatus, setPendingStatus] = useState({});  // id -> value
  const [saving, setSaving]               = useState({});  // id -> bool
  const [saveError, setSaveError]         = useState({});  // id -> message

  // add-symbol dialog state
  const [dialogOpen, setDialogOpen]             = useState(false);
  const [dlgExchanges, setDlgExchanges]         = useState([]);
  const [dlgExchangeId, setDlgExchangeId]       = useState('');
  const [dlgSymbols, setDlgSymbols]             = useState([]);
  const [dlgSymbolId, setDlgSymbolId]           = useState('');
  const [dlgContractType, setDlgContractType]   = useState('perpetual');
  const [dlgStreamType, setDlgStreamType]       = useState('trades');
  const [dlgStatus, setDlgStatus]               = useState('ACTIVE');
  const [dlgSymbolsLoading, setDlgSymbolsLoading] = useState(false);
  const [dlgError, setDlgError]                 = useState(null);
  const [dlgSaving, setDlgSaving]               = useState(false);

  const authHeader = { 'x-user-id': user?.sub };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        symbol:   symbolFilter,
        exchange: exchangeFilter,
        status:   statusFilter,
        page,
        pageSize,
      });
      const res  = await fetch(`${API_BASE_URL}/api/admin/orderbook-streaming?${params}`, { headers: authHeader });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load');
      setRows(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      // reset pending edits on fresh load
      setPendingStatus({});
      setSaving({});
      setSaveError({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbolFilter, exchangeFilter, statusFilter, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    // fetchRows will fire via useEffect because page or filter changed
  };

  const handlePageSizeChange = (val) => {
    setPageSize(val);
    setPage(1);
  };

  const handleStatusChange = (id, value) => {
    setPendingStatus(prev => ({ ...prev, [id]: value }));
    setSaveError(prev => ({ ...prev, [id]: null }));
  };

  const handleSave = async (row) => {
    const newStatus = pendingStatus[row.id];
    if (!newStatus || newStatus === row.status) return;
    setSaving(prev => ({ ...prev, [row.id]: true }));
    setSaveError(prev => ({ ...prev, [row.id]: null }));
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/orderbook-streaming/${row.id}`, {
        method:  'PATCH',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Update failed');
      // update row in place
      setRows(prev => prev.map(r =>
        r.id === row.id
          ? { ...r, status: data.data.status, timestamp_last_update: data.data.timestamp_last_update }
          : r
      ));
      setPendingStatus(prev => { const n = { ...prev }; delete n[row.id]; return n; });
    } catch (err) {
      setSaveError(prev => ({ ...prev, [row.id]: err.message }));
    } finally {
      setSaving(prev => ({ ...prev, [row.id]: false }));
    }
  };

  const openDialog = async () => {
    setDialogOpen(true);
    setDlgExchangeId('');
    setDlgSymbols([]);
    setDlgSymbolId('');
    setDlgContractType('perpetual');
    setDlgStreamType('trades');
    setDlgStatus('ACTIVE');
    setDlgError(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/orderbook-streaming/exchanges`, { headers: authHeader });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDlgExchanges(data.data);
    } catch (err) {
      setDlgError('Failed to load exchanges: ' + err.message);
    }
  };

  const handleDlgExchangeChange = async (exchangeId) => {
    setDlgExchangeId(exchangeId);
    setDlgSymbolId('');
    setDlgSymbols([]);
    if (!exchangeId) return;
    setDlgSymbolsLoading(true);
    setDlgError(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/orderbook-streaming/available-symbols?exchangeId=${exchangeId}`, { headers: authHeader });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDlgSymbols(data.data);
    } catch (err) {
      setDlgError('Failed to load symbols: ' + err.message);
    } finally {
      setDlgSymbolsLoading(false);
    }
  };

  const handleDlgSubmit = async (e) => {
    e.preventDefault();
    if (!dlgExchangeId || !dlgSymbolId) {
      setDlgError('Exchange and symbol are required.');
      return;
    }
    setDlgSaving(true);
    setDlgError(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/orderbook-streaming`, {
        method:  'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          symbolId:     parseInt(dlgSymbolId),
          exchangeId:   parseInt(dlgExchangeId),
          contractType: dlgContractType || null,
          streamType:   dlgStreamType || null,
          status:       dlgStatus,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDialogOpen(false);
      fetchRows();
    } catch (err) {
      setDlgError('Failed to insert: ' + err.message);
    } finally {
      setDlgSaving(false);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const SaveIcon = ({ spinning }) => spinning
    ? (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ animation: 'spin 1s linear infinite' }}>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    ) : (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
        <polyline points="17 21 17 13 7 13 7 21"/>
        <polyline points="7 3 7 8 15 8"/>
      </svg>
    );

  const inputStyle = {
    padding: '6px 10px', background: '#0d1117', border: '1px solid #30363d',
    borderRadius: '6px', color: '#e6edf3', fontSize: '13px', outline: 'none',
  };

  const thStyle = {
    padding: '8px 10px', fontWeight: 500, color: '#8b949e',
    borderBottom: '1px solid #30363d', textAlign: 'left', whiteSpace: 'nowrap',
  };

  const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #21262d', fontSize: '13px' };

  return (
    <div style={{ padding: '24px', color: '#e6edf3', fontFamily: 'inherit' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>Orderbook Streaming</h1>
          <p style={{ color: '#8b949e', fontSize: '13px', margin: 0 }}>
            symbols_streaming table — {total} rows total
          </p>
        </div>
        <button
          onClick={openDialog}
          style={{ ...inputStyle, cursor: 'pointer', background: '#238636', color: '#ffffff', border: '1px solid #2ea043', padding: '8px 16px', whiteSpace: 'nowrap' }}>
          + Add Symbol
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={handleFilterSubmit}
        style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, width: '180px' }}
          placeholder="Symbol name..."
          value={symbolFilter}
          onChange={e => setSymbolFilter(e.target.value)}
        />
        <input
          style={{ ...inputStyle, width: '180px' }}
          placeholder="Exchange name..."
          value={exchangeFilter}
          onChange={e => setExchangeFilter(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button type="submit"
          style={{ ...inputStyle, cursor: 'pointer', background: '#21262d', border: '1px solid #30363d' }}>
          Filter
        </button>
        {(symbolFilter || exchangeFilter || statusFilter) && (
          <button type="button"
            onClick={() => { setSymbolFilter(''); setExchangeFilter(''); setStatusFilter(''); setPage(1); }}
            style={{ ...inputStyle, cursor: 'pointer', background: 'none', color: '#8b949e', border: '1px solid #30363d' }}>
            Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#8b949e' }}>
          Rows per page:
          {['50', '100', 'all'].map(v => (
            <button key={v} type="button"
              onClick={() => handlePageSizeChange(v)}
              style={{
                padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                background: pageSize === v ? '#58a6ff' : '#21262d',
                color:      pageSize === v ? '#0a0b0f' : '#e6edf3',
                border:     '1px solid #30363d',
              }}>
              {v}
            </button>
          ))}
        </div>
      </form>

      {/* Table */}
      {loading ? (
        <div style={{ color: '#8b949e' }}>Loading...</div>
      ) : error ? (
        <div style={{ color: '#f85149' }}>{error}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>SYMBOL ID</th>
                  <th style={thStyle}>SYMBOL NAME</th>
                  <th style={thStyle}>EXCHANGE ID</th>
                  <th style={thStyle}>EXCHANGE NAME</th>
                  <th style={thStyle}>CONTRACT TYPE</th>
                  <th style={thStyle}>STREAMING TYPE</th>
                  <th style={thStyle}>LAST UPDATE</th>
                  <th style={{ ...thStyle, minWidth: '260px' }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#8b949e' }}>No rows found</td>
                  </tr>
                ) : rows.map(row => {
                  const pending     = pendingStatus[row.id];
                  const isSaving    = saving[row.id];
                  const errMsg      = saveError[row.id];
                  const isDirty     = pending && pending !== row.status;
                  const currentStatus = row.status;

                  return (
                    <tr key={row.id}
                      onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...tdStyle, color: '#8b949e' }}>{row.id}</td>
                      <td style={{ ...tdStyle, color: '#8b949e' }}>{row.symbol_id}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{row.symbol_name || '—'}</td>
                      <td style={{ ...tdStyle, color: '#8b949e' }}>{row.exchange_id}</td>
                      <td style={tdStyle}>{row.exchange_name || '—'}</td>
                      <td style={{ ...tdStyle, color: '#8b949e' }}>{row.contract_type}</td>
                      <td style={{ ...tdStyle, color: '#8b949e' }}>{row.streaming_type}</td>
                      <td style={{ ...tdStyle, color: '#8b949e', whiteSpace: 'nowrap' }}>{fmtDate(row.timestamp_last_update)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: 600,
                            color: STATUS_COLOR[currentStatus] || '#8b949e',
                            minWidth: '120px',
                          }}>
                            {currentStatus}
                          </span>
                          <select
                            value={pending ?? currentStatus}
                            onChange={e => handleStatusChange(row.id, e.target.value)}
                            style={{
                              ...inputStyle, padding: '3px 6px', fontSize: '12px',
                              cursor: 'pointer', minWidth: '150px',
                            }}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSave(row)}
                            disabled={!isDirty || isSaving}
                            title={isDirty ? `Save: ${pending}` : 'No change'}
                            style={{
                              background: 'none', border: 'none', cursor: isDirty ? 'pointer' : 'default',
                              color: isDirty ? '#3fb950' : '#484f58',
                              padding: '2px', display: 'inline-flex', alignItems: 'center',
                            }}
                          >
                            <SaveIcon spinning={isSaving} />
                          </button>
                          {errMsg && (
                            <span style={{ color: '#f85149', fontSize: '11px' }} title={errMsg}>⚠</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageSize !== 'all' && totalPages > 1 && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '16px', fontSize: '13px', color: '#8b949e' }}>
              <button
                onClick={() => setPage(1)} disabled={page === 1}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>
                «
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>
                ‹
              </button>
              <span style={{ padding: '0 8px' }}>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>
                ›
              </button>
              <button
                onClick={() => setPage(totalPages)} disabled={page === totalPages}
                style={{ ...inputStyle, padding: '4px 8px', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>
                »
              </button>
            </div>
          )}
        </>
      )}
      {/* Add Symbol Dialog */}
      {dialogOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={e => { if (e.target === e.currentTarget) setDialogOpen(false); }}
        >
          <div style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: '8px',
            padding: '24px', width: '460px', maxWidth: '90vw',
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: 600, color: '#e6edf3' }}>
              Add Symbol to Streaming
            </h2>
            <form onSubmit={handleDlgSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                {/* Exchange */}
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: '12px', marginBottom: '5px' }}>Exchange *</label>
                  <select
                    value={dlgExchangeId}
                    onChange={e => handleDlgExchangeChange(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  >
                    <option value="">— Select exchange —</option>
                    {dlgExchanges.map(ex => (
                      <option key={ex.id} value={ex.id}>{ex.exchange_name}</option>
                    ))}
                  </select>
                </div>

                {/* Symbol */}
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: '12px', marginBottom: '5px' }}>
                    Symbol *{dlgExchangeId && !dlgSymbolsLoading ? ` — ${dlgSymbols.length} available` : ''}
                  </label>
                  {dlgSymbolsLoading ? (
                    <div style={{ color: '#8b949e', fontSize: '13px', padding: '6px 0' }}>Loading symbols…</div>
                  ) : (
                    <select
                      value={dlgSymbolId}
                      onChange={e => setDlgSymbolId(e.target.value)}
                      disabled={!dlgExchangeId}
                      style={{ ...inputStyle, width: '100%', opacity: !dlgExchangeId ? 0.45 : 1, cursor: dlgExchangeId ? 'pointer' : 'not-allowed' }}
                    >
                      <option value="">— Select symbol —</option>
                      {dlgSymbols.map(s => (
                        <option key={s.id} value={s.id}>{s.symbol}</option>
                      ))}
                    </select>
                  )}
                  {dlgSymbolId && (() => {
                    const sym = dlgSymbols.find(s => String(s.id) === dlgSymbolId);
                    if (!sym) return null;
                    return (
                      <div style={{
                        marginTop: '6px', padding: '7px 10px', background: '#0d1117',
                        borderRadius: '4px', border: '1px solid #21262d',
                        fontSize: '12px', color: '#8b949e', display: 'flex', gap: '20px',
                      }}>
                        <span>Price: <span style={{ color: '#e6edf3', fontFamily: "'Courier New', Courier, monospace" }}>{sym.current_price ?? '—'}</span></span>
                        <span>Total volume 24h [USD]: <span style={{ color: '#e6edf3', fontFamily: "'Courier New', Courier, monospace" }}>{sym.total_volume != null ? Number(sym.total_volume).toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : '—'}</span></span>
                      </div>
                    );
                  })()}
                </div>

                {/* Contract Type */}
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: '12px', marginBottom: '5px' }}>Contract Type</label>
                  <select
                    value={dlgContractType}
                    onChange={e => setDlgContractType(e.target.value)}
                    style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}
                  >
                    <option value="perpetual">perpetual</option>
                    <option value="spot">spot</option>
                  </select>
                </div>

                {/* Stream Type */}
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: '12px', marginBottom: '5px' }}>Stream Type</label>
                  <select
                    value={dlgStreamType}
                    onChange={e => setDlgStreamType(e.target.value)}
                    style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}
                  >
                    <option value="trades">trades</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: '12px', marginBottom: '5px' }}>Status *</label>
                  <select
                    value={dlgStatus}
                    onChange={e => setDlgStatus(e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {dlgError && (
                  <div style={{ color: '#f85149', fontSize: '12px' }}>{dlgError}</div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <button type="button" onClick={() => setDialogOpen(false)}
                    style={{ ...inputStyle, cursor: 'pointer', background: '#21262d' }}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={dlgSaving || !dlgExchangeId || !dlgSymbolId}
                    style={{
                      ...inputStyle, cursor: (dlgSaving || !dlgExchangeId || !dlgSymbolId) ? 'not-allowed' : 'pointer',
                      background: '#238636', color: '#ffffff', border: '1px solid #2ea043',
                      opacity: (!dlgExchangeId || !dlgSymbolId) ? 0.5 : 1,
                    }}>
                    {dlgSaving ? 'Inserting…' : 'Insert'}
                  </button>
                </div>

              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderbookStreaming;
